import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { useDebounceFn, useLatest } from 'ahooks';
import { cn } from '@rocco/utils/cn';
import { Input } from '@rocco/components/input';
import { ScrollArea } from '@rocco/components/scroll-area';
import type { MeUser } from '~/server/types';
import type { BuildHubProject, Conversation } from '../../types';
import { filterChats } from '../../utils';
import { ChatInboxFilter } from './ChatInboxFilter';
import {
    ChatInboxListItem,
    ChatInboxListItemSkeleton,
} from './ChatInboxListItem';

/**
 * The chat inbox list. This is an infinite scrolling list of conversations
 * in the current user's personal inbox or company inbox.
 * @see {@link ChatInboxDrawer}
 */
export function ChatInboxList({
    className,
    conversations,
    user,
    professionalID,
    isLoading = false,
    hasMore = false,
    onFiltersChange,
    onLoadMore,
}: {
    className?: string;
    conversations: Conversation[];
    user: MeUser;
    professionalID: number | null;
    isLoading?: boolean;
    hasMore?: boolean;
    onFiltersChange?: (filters: {
        search: string;
        isArchived: boolean;
        projectIDs: string[];
    }) => void;
    onLoadMore?: () => void;
}) {
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const isMounted = useRef(false);
    const handleFiltersChangeRef = useLatest(handleFiltersChange);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'archived'>('all');
    const [selectedProjects, setSelectedProjects] = useState<BuildHubProject[]>(
        [],
    );
    const filteredConversations = useMemo(() => {
        return filterChats({
            conversations,
            userID: user.ID,
            professionalID,
            search,
            projectIDs: selectedProjects.map((project) => project.ID),
        });
    }, [conversations, professionalID, search, selectedProjects, user.ID]);
    const {
        run: setSearchDebounced,
        flush: flushSearch,
        cancel: cancelSearch,
    } = useDebounceFn(
        (search: string) => {
            handleFiltersChange({ search });
        },
        { wait: 300 },
    );

    function handleFiltersChange({
        search: newSearch,
        status,
        projects,
    }: {
        search?: string;
        status?: 'all' | 'archived';
        projects?: BuildHubProject[];
    }) {
        cancelSearch();
        if (status !== undefined) {
            setStatusFilter(status);
        }
        if (projects !== undefined) {
            setSelectedProjects(projects);
        }
        onFiltersChange?.({
            search: newSearch ?? search,
            isArchived: (status ?? statusFilter) === 'archived',
            projectIDs: (projects ?? selectedProjects).map(
                (project) => project.ID,
            ),
        });
    }

    // Scroll to top when the filters change.
    useEffect(() => {
        scrollAreaRef.current?.scrollTo({
            top: 0,
            behavior: 'instant',
        });
    }, [search, statusFilter, selectedProjects]);

    // Trigger load more conversations when the user scrolls to the bottom.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollArea = scrollAreaRef.current;
        if (!sentinel || !scrollArea || !hasMore || isLoading) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    entry?.isIntersecting &&
                    onLoadMore &&
                    hasMore &&
                    !isLoading
                ) {
                    onLoadMore();
                }
            },
            { root: scrollArea },
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isLoading, onLoadMore]);

    // Reset conversation filters when the user switches between personal and
    // company inboxes.
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        setSearch('');
        handleFiltersChangeRef.current({
            search: '',
            status: 'all',
            projects: [],
        });
    }, [professionalID, handleFiltersChangeRef]);

    return (
        <section className={cn('flex flex-col gap-6', className)}>
            <Input
                className="h-12 rounded-full px-12 font-normal placeholder:font-normal md:!text-4"
                color="light"
                placeholder="Search messages"
                prefix={<SearchIcon className="size-5 opacity-40" />}
                postfix={
                    <ChatInboxFilter
                        className="pointer-events-auto"
                        statusFilter={statusFilter}
                        selectedProjects={selectedProjects}
                        onStatusFilterChange={(status) =>
                            handleFiltersChange({ status })
                        }
                        onProjectsChange={(projects) =>
                            handleFiltersChange({ projects })
                        }
                    />
                }
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setSearchDebounced(e.target.value.trim());
                    if (!e.target.value.trim()) {
                        flushSearch();
                    }
                }}
            />
            <ScrollArea
                ref={scrollAreaRef}
                className={cn(
                    'min-h-0 flex-1',
                    // Fix for width overflow issue.
                    // https://github.com/radix-ui/primitives/issues/2722
                    '[&>div>div]:!block',
                )}
                scrollBarClassName="w-2 translate-x-2"
                thumbClassName="bg-gray-500"
                overscroll="contain"
            >
                <ul
                    className="relative flex flex-col divide-y divide-gray-300"
                    aria-label="Chat inbox"
                    aria-live="polite"
                    aria-busy={isLoading}
                >
                    {filteredConversations.length === 0 && isLoading
                        ? Array.from({ length: 8 }).map((_, index) => (
                              <ChatInboxListItemSkeleton key={index} />
                          ))
                        : filteredConversations.map((conversation) => (
                              <ChatInboxListItem
                                  key={conversation.ID}
                                  conversation={conversation}
                                  professionalID={professionalID}
                              />
                          ))}
                    {hasMore && filteredConversations.length > 0 && (
                        <div
                            ref={loadMoreSentinelRef}
                            className="invisible absolute inset-x-0 bottom-120 h-px"
                            aria-hidden="true"
                        ></div>
                    )}
                </ul>
            </ScrollArea>
        </section>
    );
}
